/**
 * Canonical enum-like constant arrays for the AI Leasing OS.
 *
 * Use the `as const` arrays at runtime; derive types from them in `./types.ts`
 * so the Zod schemas, the DB enums, and the TS types all stay in sync from
 * a single source.
 */

export const CHANNELS = [
  "whatsapp",
  "instagram",
  "messenger",
  "marketplace",
] as const;

export const SOURCE_PLATFORMS = ["marketplace"] as const;

/**
 * Wider source-attribution channels used by the Cross-Channel WhatsApp
 * Handoff feature. Stored as a free-form `VARCHAR(40)` on
 * `conversations.source_channel` and `channel_handoffs.source_channel` —
 * the SQL column is intentionally NOT enum-typed so we can extend the
 * list without a migration. This `as const` tuple is the canonical
 * runtime list; the matching `SourceChannel` type is derived in
 * `./types.ts`.
 */
export const SOURCE_CHANNELS = [
  "website",
  "listing_page",
  "instagram_profile",
  "facebook_page",
  "instagram_dm",
  "messenger",
  "marketplace",
  "email",
  "sms",
  "other",
] as const;

export const CONTACT_STATUSES = [
  "prospect",
  "lead",
  "qualified",
  "viewing_scheduled",
  "visited",
  "negotiation",
  "deposit_pending",
  "client",
  "lost",
] as const;

export const AWARENESS_LEVELS = [
  "UNAWARE",
  "PROBLEM_AWARE",
  "SOLUTION_AWARE",
  "PRODUCT_AWARE",
  "MOST_AWARE",
] as const;

export const LEAD_TEMPERATURES = ["cold", "warm", "hot", "very_hot"] as const;

export const PROPERTY_STATUSES = [
  "available",
  "pending",
  "rented",
  "unavailable",
  "archived",
] as const;

export const PROPERTY_TYPES = [
  "villa",
  "apartment",
  "house",
  "room",
  "land",
] as const;

export const VISIT_STATUSES = [
  "scheduled",
  "completed",
  "cancelled",
  "no_show",
] as const;

export const VISIT_TYPES = ["physical", "virtual"] as const;

export const MESSAGE_SENDERS = ["contact", "ai", "human", "system"] as const;

export const MESSAGE_DIRECTIONS = ["inbound", "outbound"] as const;

export const MEDIA_TYPES = ["image", "video", "audio", "document"] as const;

export const KNOWLEDGE_CATEGORIES = [
  "payment_terms",
  "viewing_rules",
  "qualification_scripts",
  "objection_handling",
  "faq",
  "area_knowledge",
  "sales_scripts",
  "company_rules",
  "contract_rules",
  "property_matching_rules",
] as const;

/**
 * Canonical AI output enums (used by the response engine, V1 stub in Phase 1).
 */
// AI intents are cross-vertical. The villa-leaning ones are kept for
// backward compatibility; new generic ones (booking_request, action_request,
// info_request) make the list usable for restaurants, spas, dentists, cafes
// and any other service business. Each vertical template's prompt addendum
// tells the model which intents it should prefer.
export const AI_INTENTS = [
  "greeting",
  "exploration",
  "qualification",
  "availability_question",
  "viewing_request",
  "booking_request",
  "action_request",
  "info_request",
  "payment_question",
  "contract_question",
  "complaint",
  "follow_up",
  "goodbye",
  "other",
] as const;

export const AI_SUGGESTED_NEXT_ACTIONS = [
  "continue_qualification",
  "send_property_info",
  "suggest_alternatives",
  "propose_visit",
  "await_human",
  "no_action",
] as const;

/**
 * Persisted next-action vocabulary used by `contact_next_actions.action`
 * and the prompt-builder's `PENDING_NEXT_ACTION` block. This is a
 * stricter, longer-term key than `AI_SUGGESTED_NEXT_ACTIONS` (which is the
 * AI's per-turn hint). The router maps an `AIIntent` → one of these when
 * persisting a row.
 */
export const NEXT_ACTIONS = [
  "send_photos",
  "share_pricing",
  "propose_visit",
  "share_location",
  "share_contract",
  "await_deposit",
  "follow_up",
  "custom",
] as const;

export const AI_MISSING_FIELDS = [
  "move_in_date",
  "length_of_stay_months",
  "budget",
  "preferred_area",
  "bedrooms_needed",
  "visit_preference",
] as const;

/**
 * Activity log event type taxonomy. The full canonical list lives in
 * `10-Operations-Logs/REFERENCE.md` and `.skills/activity-logging/logging-rules.md`.
 *
 * Phase 1 ships the V1 list. New event types require updating those docs +
 * this constant in the same change.
 */
export const ACTIVITY_LOG_EVENT_TYPES = [
  // AI
  "ai_reply",
  "ai_draft",
  "ai_failure",
  "openrouter_failure",
  "ai_invalid_output",

  // Human
  "human_reply",
  "takeover_enabled",
  "takeover_resumed",
  "takeover_disabled",
  "manual_extracted_field_update",

  // Scoring / pipeline
  "lead_score_changed",
  "score_change",
  "awareness_change",
  "temperature_change",
  "temperature_demotion",
  "status_change",
  "contact_status_changed",

  // Property
  "property_create",
  "property_update",
  "property_status_changed",
  "property_media_added",
  "property_media_removed",

  // Knowledge
  "knowledge_create",
  "knowledge_update",
  "knowledge_deactivate",
  "knowledge_retrieval_unknown_category",
  "knowledge_scrape_ok",
  "knowledge_scrape_failed",

  // Bulk imports
  "contacts_imported",
  "properties_imported",

  // Visits
  "visit_created",
  "visit_updated",
  "visit_cancelled",
  "visit_completed",
  "visit_confirmation_unresolved",

  // Channels / transport
  "webhook_invalid_signature",
  "webhook_invalid_secret",
  "webhook_unparseable",
  "webhook_failed",
  "outbound_failure",
  "outbound_send_failed",
  "outbound_noop_marketplace",
  "channel_disconnected",

  // Queue
  "queue_failure",
  "queue_job_failed",
  "takeover_race_avoided",
  "takeover_race_avoided_post_ai",
  "side_effect_partial_failure",
  "webhook_misconfigured",
  "template_applied",

  // Auth
  "auth_failed",
  "auth_failure",
  "auth_success",

  // Marketplace / source
  "marketplace_lead",

  // Identity
  "identity_merge_candidate",
  "identity_merge_applied",
  "identity_merge_failed",
  "identity_conflict",

  // Cross-channel handoff + memory (Phase H1+)
  "handoff_created",
  "handoff_used",
  "handoff_expired",
  "handoff_failed",
  "whatsapp_identity_attached",
  "identity_attached_via_handoff",
  "identity_attached_via_phone",
  "duplicate_contact_prevented",
  "whatsapp_entry_link_generated",
  "whatsapp_phone_captured",
  "conversation_context_transferred",
  "handoff_memory_attached",
  "cross_channel_continuity_established",
  "contact_interest_recorded",
  "next_action_recorded",
  "conversation_summary_updated",

  // Notifications
  "notification_created",
  "notification_ack",

  // Follow-ups
  "followup_scheduled",
  "followup_fired",
  "followup_failed",

  // Lead intelligence
  "lead_intelligence_extracted",

  // Settings
  "settings_updated",
  "business_setting_update",

  // Generic
  "request",
  "error",
] as const;

/**
 * Runtime setting keys — used as a quick reference when a service reads a
 * row from a runtime-configurable table. The string keys match table names.
 *
 * Phase 1 ships the table list; the services that consume each are wired in
 * later phases.
 */
export const RUNTIME_SETTING_KEYS = [
  "lead_scoring_rules",
  "human_takeover_rules",
  "qualification_fields",
  "property_matching_rules",
  "followup_rules",
  "ai_behavior_settings",
  "ai_model_routing",
  "channel_auto_reply_settings",
  "business_settings",
] as const;

/**
 * Canonical AI task names — keys of the `ai_model_routing` table.
 *
 * Resolution order (most specific wins):
 *   1. ai_model_routing[<task>] row with is_active = 1
 *   2. ai_behavior_settings.model (per-business default)
 *   3. env.OPENROUTER_MODEL (per-deploy default)
 *
 * Adding a new task = add it here, add a routing row in DB, and call
 * `aiRouting.resolveModel(env, "<task>")` from the code path.
 */
export const AI_TASKS = [
  "unified_reply",        // Phase 3: the single AI call per inbound message
  "field_extraction",     // Reserved — cheap pre-extraction (Phase 4+)
  "intent_classification",// Reserved — single-label intent (Phase 4+)
  "followup_message",     // Reserved — follow-up cron message gen (Phase 4)
  "escalation_check",     // Reserved — boolean safety classifier (Phase 4)
] as const;

/**
 * Default ULID for the seed business. Crockford base32, no I/L/O/U. Used in
 * `seed.sql` and referenced from any code path that needs to address the
 * single V1 business (V2 will scope by `business_id` everywhere).
 */
export const DEFAULT_BUSINESS_ID = "01HVBNSXC0000000000000000A";

/**
 * Stable business_id per vertical template slug. Each demo / vertical
 * lives in its own data namespace so switching templates in the dashboard
 * shows ONLY that vertical's contacts, conversations, visits, properties,
 * and notifications.
 *
 * villa-rental maps to the historical DEFAULT_BUSINESS_ID so all existing
 * pre-templates test data flows under villa-rental automatically.
 *
 * IDs are CHAR(26) ULID-shaped strings so they fit existing schema fields
 * without a migration.
 */
export const BUSINESS_ID_BY_TEMPLATE = {
  "villa-rental": "01HVBNSXC0000000000000000A",
  restaurant:    "01TPL000RESTAURANT00000000",
  spa:           "01TPL00000000000000000SPA0",
  dentist:       "01TPL00000000000000DENTIST",
  cafe:          "01TPL000000000000000000CAF",
  automobile:    "01TPL000AUTOMOBILE00000000",
} as const;

export type TemplateSlug = keyof typeof BUSINESS_ID_BY_TEMPLATE;

/**
 * Resolve a template slug to its business_id. Falls back to
 * DEFAULT_BUSINESS_ID when the slug is null / unknown so brand-new
 * deployments (no template applied yet) still write somewhere.
 */
export function businessIdForTemplate(slug: string | null | undefined): string {
  if (!slug) return DEFAULT_BUSINESS_ID;
  return (
    (BUSINESS_ID_BY_TEMPLATE as Record<string, string>)[slug] ??
    DEFAULT_BUSINESS_ID
  );
}

/**
 * Defaults for business-level fields. These mirror `business_settings` seed
 * values; they are NOT used as runtime fallbacks (the DB row wins).
 */
export const DEFAULT_LANGUAGE = "en" as const;
export const DEFAULT_TIMEZONE = "Asia/Makassar" as const;
export const DEFAULT_CURRENCY = "IDR" as const;
export const DEFAULT_OPENROUTER_MODEL = "anthropic/claude-sonnet-4.6" as const;

// ---------------------------------------------------------------------------
// Cross-Channel WhatsApp Handoff — token + marker helpers (Phase H1)
// ---------------------------------------------------------------------------

/**
 * Prefix for handoff tokens. Tokens look like `hf_<12+ base32 chars>` and
 * appear in the wa.me prefilled text wrapped in a `[#hf_xxx]` marker so the
 * WAHA normalizer (Phase H2) can extract and strip them.
 */
export const HANDOFF_TOKEN_PREFIX = "hf_" as const;

/**
 * Default time-to-live for an issued handoff token. After expiry the
 * `channel-handoff.service.consumeToken` call returns
 * `{ ok: false, reason: "expired" }` and the resolver falls back to the
 * normal new-contact path. Operators can override per-call.
 */
export const HANDOFF_DEFAULT_TTL_HOURS = 72;

/**
 * Regex used by the WAHA normalizer (Phase H2) to extract a handoff token
 * from the inbound message text. Matches `[#hf_<12-40 base32 chars>]`. The
 * normalizer strips the entire match (including the brackets) before
 * forwarding `messageText` to the router.
 *
 * Anchored neither at start nor end — the marker can appear anywhere in
 * the message (users typically leave it at the end, but they might edit
 * the text around it).
 */
export const HANDOFF_MARKER_RE = /\[#hf_([A-Za-z0-9]{8,40})\]/;

/**
 * Regex for non-handoff source-attribution markers, e.g.
 * `[#ref:ig_profile_whatsapp]`. These tag a click for analytics without
 * binding to an existing contact (no DB row issued). The normalizer
 * extracts the captured group into `attribution.ref`.
 */
export const REF_MARKER_RE = /\[#ref:([A-Za-z0-9_\-.:]{1,120})\]/;
